import { css, cx } from '@linaria/core'
import {
  BodyText,
  Button,
  ButtonGroup,
  elFlex1,
  elHFull,
  elMt8,
  FlexContainer,
  FormLayout,
  InputGroup,
  InputWrapFull,
  Label,
  TextArea,
} from '@reapit/elements'
import { DialogWrapper } from '@reapitform/common-ui'
import {
  TWithdrawAgreement,
  useWithdrawAgreement,
  withdrawAgreementSchema,
} from '../../api/documents/actions/withdraw-agreement'
import React from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

interface IWithhDrawProps {
  isOpen?: boolean
  onClose?: () => void
  onFinish?: (agreementIds: string[]) => void
  agreementId?: string[]
}

export const WithdrawModal: React.FC<IWithhDrawProps> = ({ isOpen = false, onClose, onFinish, agreementId }) => {
  const { withdrawAgreement, isLoading } = useWithdrawAgreement()

  const { handleSubmit, register, setValue, watch, reset } = useForm<TWithdrawAgreement>({
    defaultValues: {
      withdrawalReason: '',
      notifyRecipients: false,
      iDs: undefined,
    },
    resolver: yupResolver(withdrawAgreementSchema),
  })

  React.useEffect(() => {
    if (!agreementId) return
    setValue('iDs', agreementId)
  }, [agreementId])

  const confirmationMessage: string = React.useMemo(() => {
    if (!agreementId?.length) return ''
    const agreementLength: number = agreementId.length
    const isHaveMoreThan1: boolean = Boolean(agreementLength && agreementId.length > 1)

    if (isHaveMoreThan1)
      return `You have selected ${agreementLength} agreements. Withdrawing these agreements will cancel any pending actions.`
    return 'Withdrawing this agreement will cancel any pending actions.'
  }, [agreementId?.length])

  if (!agreementId || !agreementId.length) return

  const onSubmit = async (data: TWithdrawAgreement) => {
    await withdrawAgreement(data)
    onFinish?.(agreementId)
    onClose?.()
    reset()
  }

  const handleClose = () => {
    onClose?.()
    reset()
  }

  return (
    <DialogWrapper
      handlerModalClose={handleClose}
      isModalOpen={isOpen}
      title="Withdraw Agreement"
      className={css`
        width: 400px;
      `}
    >
      <FlexContainer isFlexColumn className={cx(elHFull, elFlex1)}>
        <BodyText hasSectionMargin>{confirmationMessage}</BodyText>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormLayout>
            <InputWrapFull>
              <InputGroup>
                <Label>Reason</Label>
                <TextArea
                  {...register('withdrawalReason')}
                  className={css`
                    resize: none;
                    min-height: 3.5em;
                  `}
                />
              </InputGroup>
            </InputWrapFull>
            <InputWrapFull>
              <InputGroup
                inputAddOnText="Notify signees by email"
                type="checkbox"
                checked={watch('notifyRecipients')}
                onChange={(event) => setValue('notifyRecipients', event.target.checked)}
              />
            </InputWrapFull>
          </FormLayout>
          <ButtonGroup alignment="right" className={cx(elMt8)}>
            <Button intent="default" disabled={isLoading} onClick={handleClose}>
              Cancel
            </Button>
            <Button loading={isLoading} disabled={isLoading} intent="primary" type="submit">
              Withdraw
            </Button>
          </ButtonGroup>
        </form>
      </FlexContainer>
    </DialogWrapper>
  )
}
